import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useProfile } from '@/shared/hooks/useCloudData';
import { usePaymentStats } from '@/features/payments/hooks/usePayments';
import { useInvoices } from '@/features/invoices/hooks/useInvoices';
import { useAppointments, useSaveAppointment } from '@/features/appointments/hooks/useAppointments';
import { useLowStockProducts } from '@/features/inventory/hooks/useProducts';
import { useStudio } from '@/features/studio/contexts/StudioContext';
import { useClients } from '@/features/clients/hooks/useClients';
import { ClientFormDialog } from '@/features/clients/components/ClientFormDialog';
import { motion } from 'framer-motion';
import {
  Plus, TrendingUp, Receipt, Clock, AlertTriangle,
  Play, Pause, CalendarDays, Banknote, Package, ArrowRight,
  Users, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format, differenceInDays, startOfMonth, endOfMonth, addDays, isSameDay, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { OnboardingFlow, useOnboardingDone } from '@/features/onboarding/components/OnboardingFlow';
import { useSubscription } from '@/features/subscription/hooks/useSubscription';

// ─── Types ─────────────────────────────────────────────────────────────────────

type ActiveSession = {
  id: string;
  client_name: string;
  status_v2: string;
  started_at: string | null;
  paused_at: string | null;
  elapsed_seconds: number;
  clients: { first_name: string; last_name: string } | null;
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

const anim = (i: number) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: i * 0.06 },
});

function MetricCard({ icon, label, value, sub, accent }: {
  icon: React.ReactNode; label: string; value: string; sub?: string; accent?: string;
}) {
  return (
    <div className="glass-card p-4 space-y-1 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">{icon} {label}</p>
          <p className={`text-2xl font-bold font-display mt-1 tabular-nums ${accent ?? 'text-foreground'}`}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard ─────────────────────────────────────────────────────────────────

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { membership } = useStudio();
  const { isDone: onboardingDone, markDone: markOnboardingDone } = useOnboardingDone();
  const [showOnboarding, setShowOnboarding] = useState(!onboardingDone);

  // ── Payments ───────────────────────────────────────────────────────────────
  const { totalMonth, pendingTotal } = usePaymentStats();
  const { data: invoices = [] } = useInvoices();
  const unpaidInvoicesTotal = invoices
    .filter(inv => inv.status === 'sent')
    .reduce((s, inv) => s + (inv.total_ttc ?? 0), 0);

  // ── Sessions ───────────────────────────────────────────────────────────────
  const { data: activeSessions = [] } = useQuery<ActiveSession[]>({
    queryKey: ['active-sessions-dashboard', user?.id],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('sessions')
        .select('id, client_name, status_v2, started_at, paused_at, elapsed_seconds, clients(first_name, last_name)')
        .eq('owner_id', user!.id)
        .in('status_v2', ['in_progress', 'paused'])
        .order('started_at', { ascending: false });
      return data || [];
    },
    enabled: !!user,
    staleTime: 0,
    refetchInterval: 30_000,
  });

  const { data: monthSessionsData = [] } = useQuery<{ id: string }[]>({
    queryKey: ['sessions-month-count', user?.id],
    queryFn: async () => {
      const monthStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
      const monthEnd   = format(endOfMonth(new Date()), 'yyyy-MM-dd');
      const { data } = await (supabase as any)
        .from('sessions')
        .select('id')
        .eq('owner_id', user!.id)
        .eq('status_v2', 'completed')
        .gte('closed_at', `${monthStart}T00:00:00`)
        .lte('closed_at', `${monthEnd}T23:59:59`);
      return data || [];
    },
    enabled: !!user,
  });

  // ── Appointments & Alerts ──────────────────────────────────────────────────
  const { data: allAppts = [] } = useAppointments();
  const today = new Date();
  const todayStr2 = format(today, 'yyyy-MM-dd');
  const next7Days = Array.from({ length: 7 }, (_, i) => format(addDays(today, i), 'yyyy-MM-dd'));
  const upcomingAppts = allAppts.filter(a => next7Days.includes(a.date) && a.status !== 'cancelled');
  const todayAppts = upcomingAppts.filter(a => a.date === todayStr2);

  // group by date
  const apptsByDate = next7Days.reduce<Record<string, typeof allAppts>>((acc, d) => {
    const day = upcomingAppts.filter(a => a.date === d);
    if (day.length > 0) acc[d] = day;
    return acc;
  }, {});

  const lowStockProducts = useLowStockProducts();

  // ── +RDV Dialog ────────────────────────────────────────────────────────────
  const { data: clients = [] } = useClients();
  const saveAppointment = useSaveAppointment();
  const [rdvOpen, setRdvOpen] = useState(false);
  const [rdvSearch, setRdvSearch] = useState('');
  const [rdvClient, setRdvClient] = useState<{ id: string; name: string } | null>(null);
  const [rdvDate, setRdvDate] = useState(todayStr2);
  const [rdvTime, setRdvTime] = useState('10:00');
  const [rdvActe, setRdvActe] = useState('');
  const [rdvNotes, setRdvNotes] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [newClientOpen, setNewClientOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredClients = rdvSearch.length >= 1
    ? clients.filter(c => c.name.toLowerCase().includes(rdvSearch.toLowerCase())).slice(0, 6)
    : [];

  const resetRdvForm = () => {
    setRdvSearch(''); setRdvClient(null); setRdvDate(todayStr2);
    setRdvTime('10:00'); setRdvActe(''); setRdvNotes('');
  };

  const handleSaveRdv = () => {
    if (!rdvClient) { toast.error('Sélectionnez un client'); return; }
    if (!rdvDate || !rdvTime) { toast.error('Date et heure requises'); return; }
    saveAppointment.mutate(
      { client_name: rdvClient.name, client_id: rdvClient.id, date: rdvDate, start_time: rdvTime, project_name: rdvActe, notes: rdvNotes },
      { onSuccess: () => { toast.success('RDV créé'); setRdvOpen(false); resetRdvForm(); }, onError: () => toast.error('Erreur création RDV') }
    );
  };

  const pausedOverdue = activeSessions.filter(s => {
    if (s.status_v2 !== 'paused' || !s.paused_at) return false;
    return differenceInDays(new Date(), new Date(s.paused_at)) >= 3;
  });

  const overdueInvoices = invoices.filter(inv => {
    if (inv.status !== 'sent') return false;
    return differenceInDays(new Date(), new Date(inv.issue_date || inv.created_at)) > 30;
  });

  const alertsCount = pausedOverdue.length + overdueInvoices.length + lowStockProducts.length;

  // ── Header ─────────────────────────────────────────────────────────────────
  const { isInTrial, trialDaysLeft, isLegacyFree } = useSubscription();
  const displayName = (profile as any)?.display_name || (profile as any)?.first_name || '';
  const todayStr = format(new Date(), 'EEEE d MMMM', { locale: fr });
  const isOwner  = membership?.isOwner ?? false;

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto space-y-6">

      {/* ── Banner legacy free ───────────────────────────────────────── */}
      {isLegacyFree && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400">
          <p className="text-sm font-medium">
            Votre accès gratuit legacy expire le 1er juillet 2026 — Choisissez un plan pour continuer à utiliser Consentink.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
          >
            Choisir un plan
          </button>
        </div>
      )}

      {/* ── Banner trial ─────────────────────────────────────────────── */}
      {isInTrial && (
        trialDaysLeft > 3 ? (
          <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-700 dark:text-blue-400">
            <p className="text-sm font-medium">
              Essai gratuit — {trialDaysLeft}j restants
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
            >
              Voir les plans
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400">
            <p className="text-sm font-medium">
              Votre essai se termine dans {trialDaysLeft}j — Choisissez un plan
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
            >
              Voir les plans
            </button>
          </div>
        )
      )}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <motion.div {...anim(0)} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-display text-foreground">
            Bonjour{displayName ? ` ${displayName}` : ''} 👋
          </h1>
          <p className="text-muted-foreground mt-0.5 capitalize">{todayStr}</p>
          {membership?.studioName && (
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
              <Users size={11} className="text-primary/60" />
              {membership.studioName}
              {isOwner && (
                <span className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">propriétaire</span>
              )}
            </p>
          )}
        </div>
      </motion.div>

      {/* ── 4 Métriques ─────────────────────────────────────────────────── */}
      <motion.div {...anim(1)} className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard
          icon={<TrendingUp size={13} />}
          label="CA du mois"
          value={`${totalMonth.toFixed(0)} €`}
          accent="text-primary"
        />
        <MetricCard
          icon={<Receipt size={13} />}
          label="Factures impayées"
          value={`${unpaidInvoicesTotal.toFixed(0)} €`}
          accent={unpaidInvoicesTotal > 0 ? 'text-amber-500' : 'text-foreground'}
        />
        <MetricCard
          icon={<Clock size={13} />}
          label="Sessions ce mois"
          value={String(monthSessionsData.length)}
        />
        <MetricCard
          icon={<Banknote size={13} />}
          label="Acomptes attendus"
          value={`${pendingTotal.toFixed(0)} €`}
          accent={pendingTotal > 0 ? 'text-amber-500' : 'text-foreground'}
        />
      </motion.div>

      {/* ── Actions rapides ──────────────────────────────────────────────── */}
      <motion.div {...anim(2)} className="flex gap-2 flex-wrap">
        <Button className="gap-2 bg-green-600 hover:bg-green-700 text-white" onClick={() => navigate('/sessions/new')}>
          <Plus size={14} /> Session
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate('/appointments')}>
          <Plus size={14} /> RDV
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate('/quotes/new')}>
          <Plus size={14} /> Devis
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate('/clients?new=1')}>
          <Plus size={14} /> Client
        </Button>
      </motion.div>

      {/* ── RDV 7 prochains jours ─────────────────────────────────────────── */}
      <motion.div {...anim(3)} className="glass-card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold font-display flex items-center gap-2">
            <CalendarDays size={18} className="text-primary" />
            Rendez-vous
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-normal">{todayAppts.length}</span>
          </h2>
          <div className="flex items-center gap-2">
            <Button size="sm" className="gap-1 text-xs h-7 px-2.5" onClick={() => setRdvOpen(true)}>
              <Plus size={12} /> RDV
            </Button>
            <Button variant="ghost" size="sm" className="gap-1 text-xs text-muted-foreground h-7 px-2" onClick={() => navigate('/appointments')}>
              Voir tout <ArrowRight size={12} />
            </Button>
          </div>
        </div>
        {Object.keys(apptsByDate).length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Aucun rendez-vous cette semaine</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(apptsByDate).map(([dateStr, dayAppts]) => {
              const d = parseISO(dateStr);
              const isToday = isSameDay(d, today);
              const label = isToday ? 'Aujourd\'hui' : format(d, 'EEEE d MMM', { locale: fr });
              return (
                <div key={dateStr} className="space-y-1.5">
                  <p className={`text-xs font-semibold uppercase tracking-wide ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>{label}</p>
                  {dayAppts.map(appt => {
                    const missingConsent = !appt.client_id;
                    const hasAlertNote = appt.notes?.toLowerCase().includes('allergi');
                    return (
                      <div key={appt.id} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/40 hover:bg-secondary/60 transition-colors">
                        <div className="px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary text-sm font-mono font-semibold shrink-0 min-w-[52px] text-center">
                          {appt.start_time?.slice(0, 5) ?? '—'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{appt.client_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{appt.project_name || '—'}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {hasAlertNote && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-600 border border-red-500/20 font-medium">Allergie</span>
                          )}
                          {missingConsent && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/20 font-medium">Sans consentement</span>
                          )}
                          {!appt.is_paid && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/20 font-medium">Acompte dû</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* ── Dialog +RDV ─────────────────────────────────────────────────────── */}
      <Dialog open={rdvOpen} onOpenChange={(o) => { setRdvOpen(o); if (!o) resetRdvForm(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Nouveau rendez-vous</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Client search */}
            <div className="space-y-1.5" ref={searchRef}>
              <label className="text-sm font-medium">Client</label>
              {rdvClient ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/10 border border-primary/20">
                  <span className="flex-1 text-sm font-medium">{rdvClient.name}</span>
                  <button onClick={() => { setRdvClient(null); setRdvSearch(''); }} className="text-muted-foreground hover:text-foreground">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Rechercher un client…"
                    value={rdvSearch}
                    onChange={e => { setRdvSearch(e.target.value); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                    className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none"
                  />
                  {showSuggestions && filteredClients.length > 0 && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-xl shadow-lg overflow-hidden">
                      {filteredClients.map(c => (
                        <button key={c.id} onMouseDown={() => { setRdvClient({ id: c.id, name: c.name }); setRdvSearch(''); setShowSuggestions(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors">
                          {c.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button onClick={() => { setRdvOpen(false); setNewClientOpen(true); }}
                className="text-xs text-primary hover:underline underline-offset-2">
                + Nouveau client
              </button>
            </div>
            {/* Date + heure */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Date</label>
                <input type="date" value={rdvDate} onChange={e => setRdvDate(e.target.value)}
                  className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Heure</label>
                <input type="time" value={rdvTime} onChange={e => setRdvTime(e.target.value)}
                  className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none" />
              </div>
            </div>
            {/* Type d'acte */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Type d'acte</label>
              <input type="text" placeholder="Ex : Tatouage floral, Piercing lobe…" value={rdvActe}
                onChange={e => setRdvActe(e.target.value)}
                className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none" />
            </div>
            {/* Notes */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Notes <span className="text-muted-foreground font-normal">(optionnel)</span></label>
              <textarea rows={2} value={rdvNotes} onChange={e => setRdvNotes(e.target.value)}
                className="w-full p-2.5 bg-background border-2 border-border rounded-xl text-sm focus:border-primary focus:outline-none resize-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRdvOpen(false); resetRdvForm(); }}>Annuler</Button>
            <Button onClick={handleSaveRdv} disabled={saveAppointment.isPending}>Créer le RDV</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog nouveau client (depuis +RDV) ───────────────────────────── */}
      <ClientFormDialog
        open={newClientOpen}
        onOpenChange={(o) => { setNewClientOpen(o); if (!o) setRdvOpen(true); }}
        editing={null}
        onCreateClient={async (data) => {
          const fullName = `${data.first_name} ${data.last_name}`.trim();
          setRdvClient({ id: '', name: fullName });
          setNewClientOpen(false);
          setRdvOpen(true);
        }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* ── Alertes ─────────────────────────────────────────────────────── */}
        <motion.div {...anim(4)} className="glass-card p-5 space-y-4">
          <h2 className="font-semibold font-display flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" />
            Alertes
            {alertsCount > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 font-normal">{alertsCount}</span>
            )}
          </h2>

          {alertsCount === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Aucune alerte</p>
          ) : (
            <div className="space-y-2">
              {pausedOverdue.map(s => {
                const name = s.clients
                  ? `${s.clients.first_name} ${s.clients.last_name}`.trim()
                  : s.client_name;
                const days = differenceInDays(new Date(), new Date(s.paused_at!));
                return (
                  <button key={s.id} onClick={() => navigate(`/sessions/${s.id}`)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-left hover:bg-amber-500/15 transition-colors">
                    <Pause size={14} className="text-amber-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{name}</p>
                      <p className="text-xs text-muted-foreground">Session en pause depuis {days}j</p>
                    </div>
                    <ArrowRight size={12} className="text-muted-foreground shrink-0" />
                  </button>
                );
              })}

              {overdueInvoices.map(inv => {
                const days = differenceInDays(new Date(), new Date(inv.issue_date || inv.created_at));
                return (
                  <button key={inv.id} onClick={() => navigate('/invoices')}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-left hover:bg-destructive/15 transition-colors">
                    <Receipt size={14} className="text-destructive shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{inv.invoice_number} — {inv.client_name}</p>
                      <p className="text-xs text-muted-foreground">Impayée depuis {days}j · {(inv.total_ttc ?? 0).toFixed(0)} €</p>
                    </div>
                    <ArrowRight size={12} className="text-muted-foreground shrink-0" />
                  </button>
                );
              })}

              {lowStockProducts.map(p => (
                <button key={p.id} onClick={() => navigate('/inventory')}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-destructive/5 border border-destructive/20 text-left hover:bg-destructive/10 transition-colors">
                  <Package size={14} className="text-destructive shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground">Stock critique : {p.stock} restant(s)</p>
                  </div>
                  <ArrowRight size={12} className="text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* ── Sessions actives ─────────────────────────────────────────────── */}
        <motion.div {...anim(5)} className="glass-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold font-display flex items-center gap-2">
              <Play size={18} className="text-green-500" />
              Sessions actives
              {activeSessions.length > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-600 font-normal">{activeSessions.length}</span>
              )}
            </h2>
          </div>

          {activeSessions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Aucune session en cours</p>
          ) : (
            <div className="space-y-2">
              {activeSessions.map(s => {
                const name = s.clients
                  ? `${s.clients.first_name} ${s.clients.last_name}`.trim()
                  : s.client_name;
                const isRunning = s.status_v2 === 'in_progress';
                return (
                  <button key={s.id} onClick={() => navigate(`/sessions/${s.id}`)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-secondary/40 hover:bg-secondary/60 transition-colors text-left">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${isRunning ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{name}</p>
                      {s.started_at && (
                        <p className="text-xs text-muted-foreground">
                          Depuis {format(new Date(s.started_at), 'HH:mm', { locale: fr })}
                        </p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium shrink-0 ${
                      isRunning
                        ? 'bg-green-500/10 text-green-600 border-green-500/20'
                        : 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                    }`}>
                      {isRunning ? 'En cours' : 'En pause'}
                    </span>
                    <ArrowRight size={12} className="text-muted-foreground shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </motion.div>

      </div>

      {showOnboarding && (
        <OnboardingFlow
          onDone={() => { markOnboardingDone(); setShowOnboarding(false); }}
        />
      )}
    </div>
  );
};

export default Dashboard;
